import tinycolor from 'tinycolor2';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    extendedPalette: {
      accent: {
        main: string;
      };
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    extendedPalette?: {
      accent?: {
        main?: string;
      };
    };
  }
}

// picked colors based on help from https://www.youtube.com/watch?v=HAlIWRcldoc&t=223s&ab_channel=Juxtopposed

const primary = '#64aded';
const secondary = '#f2bf92';
const primaryText = '#000000'; // black
const secondaryText = '#b5b1b1'; // chose a lighter tint of primary text
const disabledText = '#b2b2b2'; // chose a lighter tint of primary text that is lighter than secondary text
const defaultBackground = '#ffffff'; 
const primaryLight = '#eff6fd'; // chose lightest tint of primary color that wasn't white from  https://www.color-hex.com/color/64aded
const secondaryLight = '#fcf2e9'; // chose the second lightest tint of secondary color that wasn't white
const accent = '#197bd2';

const darkPrimary = '#3e6684';
const darkSecondary = '#fcfdfc';
const darkPrimaryText = '#ffffff';
const darkSecondaryText = '#2e1805';
const darkDisabledText = '';
const darkBackground = '#000000';
const darkAccent = '#cabfb9';

const warning = '#FFC260';
const success = '#3CD4A0';
const error = '#ed5c5c';
const info = '#137AFE';

// NOTE: if the font size is less than 16px for input fields, then add this to the index.html head <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
//      this prevents the website from making the user experience of the website worse by preventing autozooming. However, it still allows users to zoom if they wanted
// TODO: need to make sure this changes to a higher body size depending of if being displayed on desktop or phone
const defaultTypgographyBodySize = 14
// https://banc.digital/blog/typography-font-sizes-styles-formats
const goldenRatio = 1.333

const fontWeightLight = 300
const fontWeightRegular = 400
const fontWeightMedium = 500
const fontWeightBold = 700

const extendedMuiPalette = {
  accent: {
    main: accent,
  },
};

const muiPalette = {
  primary: {
    main: primary,
    light: primaryLight,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: secondary,
    light: secondaryLight,
  },
  warning: {
    main: warning,
  },
  success: {
    main: success,
  },
  info: {
    main: info,
  },
  error: {
    main: error,
    contrastText: '#FFFFFF',
  },
  text: {
    primary: primaryText,
    secondary: secondaryText,
    disabled: disabledText,
  },
  background: {
    default: defaultBackground,
  },
};

const muiTheme = createTheme({
  palette: muiPalette,
  extendedPalette: extendedMuiPalette,
  typography: {
    htmlFontSize: 16,
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeightLight: fontWeightLight,
    fontWeightRegular: fontWeightRegular,
    fontWeightMedium: fontWeightMedium,
    fontWeightBold: fontWeightBold,
    h1: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 6,
      fontWeight: fontWeightMedium,
    },
    h2: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 5,
      fontWeight: fontWeightMedium,
    },
    h3: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 4,
      fontWeight: fontWeightMedium,
    },
    h4: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 3,
      fontWeight: fontWeightMedium,
    },
    h5: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 2,
      fontWeight: fontWeightMedium,
    },
    h6: {
      fontSize: defaultTypgographyBodySize * goldenRatio ** 1,
      fontWeight: fontWeightMedium,
    },
    subtitle1: {
      fontSize: defaultTypgographyBodySize / goldenRatio ** 0.75,
      fontWeight: fontWeightRegular,
    },
    subtitle2: {
      fontSize: defaultTypgographyBodySize / goldenRatio ** 1,
      fontWeight: fontWeightRegular,
    },
    body1: {
      fontSize: defaultTypgographyBodySize,
      fontWeight: fontWeightRegular,
    },
    body2: {
      fontSize: defaultTypgographyBodySize / goldenRatio ** 0.5,
      fontWeight: fontWeightRegular,
    },
  },
  components: {
    // Name of the component
    MuiTextField: {
      defaultProps: {
        // The props to change the default for.
        // disableRipple: true, // No more ripple, on the whole application 💣!
        size: 'small',
      },
      styleOverrides: {
        // Name of the slot
        root: {
          // Some CSS
          // maxHeight: '40px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: primaryText,
          borderRadius: '1rem',
          boxShadow: 'none'
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: `2px 4px 8px ${accent}`,
          borderRadius: '1rem',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
  },
});

export default muiTheme;
